import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { requireAdmin } from "@/lib/admin-guard";
import {
  listTherapistApplications,
  updateTherapistApplication,
  deleteTherapistApplication,
  setTherapistDemoFlag,
} from "@/lib/therapist-applications.functions";
import { createTherapistCheckoutSession } from "@/lib/therapist-stripe.functions";

export const Route = createFileRoute("/admin_/therapist-applications")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Therapist Applications — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminTherapistApplications,
});

type Status =
  | "pending_review"
  | "more_info_needed"
  | "approved"
  | "payment_pending"
  | "payment_received"
  | "ready_for_publication"
  | "live_in_directory"
  | "rejected";

const STATUS_LABEL: Record<Status, string> = {
  pending_review: "Pending Review",
  more_info_needed: "More Information Needed",
  approved: "Approved",
  payment_pending: "Payment Pending",
  payment_received: "Payment Received",
  ready_for_publication: "Ready for Publication",
  live_in_directory: "Live in Directory",
  rejected: "Rejected",
};

const STATUS_COLOR: Record<Status, string> = {
  pending_review: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
  more_info_needed: "bg-orange-500/20 text-orange-700 dark:text-orange-300",
  approved: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
  payment_pending: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-300",
  payment_received: "bg-teal-500/20 text-teal-700 dark:text-teal-300",
  ready_for_publication: "bg-indigo-500/20 text-indigo-700 dark:text-indigo-300",
  live_in_directory: "bg-blue-500/20 text-blue-700 dark:text-blue-300",
  rejected: "bg-red-500/20 text-red-700 dark:text-red-300",
};

type Application = {
  id: string;
  full_name: string;
  professional_title: string;
  email: string;
  website: string | null;
  country: string;
  state_region: string | null;
  licensing_jurisdiction: string | null;
  license_number: string | null;
  professional_body: string | null;
  regulatory_body: string | null;

  qualifications: string | null;
  years_experience: number | null;
  therapy_approaches: string[];
  specialisms: string[];
  languages: string[];
  availability_modes: string[];
  short_bio: string;
  photo_url: string | null;
  photo_signed_url: string | null;
  certificate_url: string | null;
  certificate_signed_url: string | null;
  insurance_certificate_url: string | null;
  insurance_certificate_signed_url: string | null;
  insurance_confirmed: boolean;
  status: Status;
  verified_badge: boolean;
  admin_notes: string | null;
  submitted_at: string;
  payment_link: string | null;
  payment_amount_cents: number | null;
  payment_currency: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  paid_at: string | null;
  approved_at: string | null;
  founding_member: boolean;
  published_at: string | null;
  stripe_checkout_session_id?: string | null;
  stripe_payment_intent_id?: string | null;
  stripe_customer_id?: string | null;
  stripe_checkout_url?: string | null;
  is_demo?: boolean;
};

const PAYMENT_STATUS: Record<Status, { label: string; color: string }> = {
  pending_review: { label: "—", color: "bg-muted text-muted-foreground" },
  more_info_needed: { label: "—", color: "bg-muted text-muted-foreground" },
  approved: { label: "Awaiting link", color: "bg-amber-500/20 text-amber-700 dark:text-amber-300" },
  payment_pending: { label: "Pending", color: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-300" },
  payment_received: { label: "Paid", color: "bg-teal-500/20 text-teal-700 dark:text-teal-300" },
  ready_for_publication: { label: "Paid", color: "bg-teal-500/20 text-teal-700 dark:text-teal-300" },
  live_in_directory: { label: "Paid", color: "bg-teal-500/20 text-teal-700 dark:text-teal-300" },
  rejected: { label: "—", color: "bg-muted text-muted-foreground" },
};

const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString() : "—";

function AdminTherapistApplications() {
  const listFn = useServerFn(listTherapistApplications);
  const updateFn = useServerFn(updateTherapistApplication);
  const deleteFn = useServerFn(deleteTherapistApplication);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Status | "all">("all");
  const [demoScope, setDemoScope] = useState<"real" | "demo" | "all">("real");
  const [openId, setOpenId] = useState<string | null>(null);

  const setDemoFn = useServerFn(setTherapistDemoFlag);
  const demoMut = useMutation({
    mutationFn: (vars: { id: string; is_demo: boolean }) => setDemoFn({ data: vars }),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ["therapist-applications"] });
      toast.success(vars.is_demo ? "Marked as demo profile" : "Marked as real profile");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["therapist-applications"],
    queryFn: () => listFn(),
  });

  type UpdateVars = {
    id: string;
    status: Status;
    admin_notes: string;
    verified_badge?: boolean;
    payment_link?: string;
    payment_amount_cents?: number;
    payment_currency?: string;
    payment_method?: string;
    payment_reference?: string;
    founding_member?: boolean;
    send_email?: boolean;
  };
  const updateMut = useMutation({
    mutationFn: (vars: UpdateVars) => updateFn({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["therapist-applications"] });
      toast.success("Application updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["therapist-applications"] });
      setOpenId(null);
      toast.success("Application deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createCheckoutFn = useServerFn(createTherapistCheckoutSession);
  const createCheckoutMut = useMutation({
    mutationFn: (vars: {
      application_id: string;
      amount_cents?: number;
      currency?: string;
      openInTab?: boolean;
      label?: string;
    }) => {
      const { openInTab: _o, label: _l, ...payload } = vars;
      void _o; void _l;
      return createCheckoutFn({ data: payload });
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ["therapist-applications"] });
      toast.success(vars.label ? `${vars.label} ready` : "Stripe Checkout link created");
      if (res?.url) {
        if (vars.openInTab) {
          window.open(res.url, "_blank", "noopener,noreferrer");
          toast.message(
            "Complete the test payment with card 4242 4242 4242 4242 — webhook will mark this application as paid within ~10s.",
          );
          // Poll for ~2 minutes so the admin sees paid_at flip without refreshing.
          let ticks = 0;
          const id = window.setInterval(() => {
            ticks += 1;
            qc.invalidateQueries({ queryKey: ["therapist-applications"] });
            if (ticks >= 30) window.clearInterval(id);
          }, 4000);
        } else {
          try {
            navigator.clipboard.writeText(res.url);
            toast.message("Checkout URL copied to clipboard");
          } catch {
            /* ignore */
          }
        }
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const allApplications = (data?.applications ?? []) as Application[];
  const scoped = allApplications.filter((a) =>
    demoScope === "all" ? true : demoScope === "demo" ? !!a.is_demo : !a.is_demo,
  );
  const applications = scoped;
  const filtered = filter === "all" ? applications : applications.filter((a) => a.status === filter);
  const counts = applications.reduce<Record<string, number>>((acc, a) => {
    acc[a.status] = (acc[a.status] ?? 0) + 1;
    return acc;
  }, {});
  const demoCounts = {
    real: allApplications.filter((a) => !a.is_demo).length,
    demo: allApplications.filter((a) => !!a.is_demo).length,
    all: allApplications.length,
  };

  const opened = openId ? applications.find((a) => a.id === openId) : null;

  return (
    <div className="min-h-dvh bg-background py-10">
      <div className="mx-auto max-w-7xl px-4">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back to admin
            </Link>
            <h1 className="mt-2 text-3xl font-serif text-foreground">Therapist Applications</h1>
            <p className="text-sm text-muted-foreground">
              Review credentials and approve practitioners for the directory.
              Forward to{" "}
              <a className="underline" href="mailto:therapists@recoverytrauma.com">
                therapists@recoverytrauma.com
              </a>{" "}
              as needed.
            </p>
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Profile type:</span>
          {(["real", "demo", "all"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setDemoScope(s)}
              className={`rounded-full border px-3 py-1 text-sm ${
                demoScope === s
                  ? "border-gold bg-gold/20 text-foreground"
                  : "border-gold/30 text-muted-foreground hover:bg-gold/10"
              }`}
            >
              {s === "real" ? "Real Therapists" : s === "demo" ? "Demo / Marketing" : "All"}
              <span className="ms-1 text-xs opacity-70">({demoCounts[s]})</span>
            </button>
          ))}
          <span className="ms-2 text-xs text-muted-foreground">
            Demo profiles remain visible in the directory but are excluded from all statistics and reports.
          </span>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {(["all", "pending_review", "approved", "payment_pending", "payment_received", "live_in_directory"] as const).map(
            (s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  filter === s
                    ? "border-gold bg-gold/20 text-foreground"
                    : "border-gold/30 text-muted-foreground hover:bg-gold/10"
                }`}
              >
                {s === "all" ? "All" : s === "live_in_directory" ? "Published" : STATUS_LABEL[s]}
                <span className="ms-1 text-xs opacity-70">
                  ({s === "all" ? applications.length : counts[s] ?? 0})
                </span>
              </button>
            ),
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-gold/20 bg-card p-10 text-center text-muted-foreground">
            No applications in this view.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gold/20 bg-card">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="bg-background/60 text-start text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Application Status</th>
                  <th className="px-4 py-3">Payment Status</th>
                  <th className="px-4 py-3">Date Applied</th>
                  <th className="px-4 py-3">Date Approved</th>
                  <th className="px-4 py-3">Date Paid</th>
                  <th className="px-4 py-3">Published</th>
                  <th className="px-4 py-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-t border-gold/10">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">
                        {a.full_name}{" "}
                        {a.verified_badge && (
                          <ShieldCheck className="inline h-3.5 w-3.5 text-gold" />
                        )}
                        {a.is_demo && (
                          <span className="ms-2 rounded-full bg-purple-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-300">
                            Demo
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{a.professional_title}</div>
                      <a className="text-xs text-blue-500 hover:underline" href={`mailto:${a.email}`}>
                        {a.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {a.country}
                      {a.state_region ? `, ${a.state_region}` : ""}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLOR[a.status]}`}
                      >
                        {STATUS_LABEL[a.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${PAYMENT_STATUS[a.status].color}`}
                      >
                        {PAYMENT_STATUS[a.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {fmtDate(a.submitted_at)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {fmtDate(a.approved_at)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {fmtDate(a.paid_at)}
                    </td>
                    <td className="px-4 py-3">
                      {a.published_at ? (
                        <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                          Yes · {fmtDate(a.published_at)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() =>
                            demoMut.mutate({ id: a.id, is_demo: !a.is_demo })
                          }
                          disabled={demoMut.isPending}
                          className="rounded-lg border border-purple-500/30 px-2 py-1 text-xs text-purple-700 hover:bg-purple-500/10 dark:text-purple-300"
                          title={a.is_demo ? "Mark as real therapist" : "Mark as demo / marketing profile"}
                        >
                          {a.is_demo ? "Unmark demo" : "Mark demo"}
                        </button>
                        <button
                          onClick={() => setOpenId(a.id)}
                          className="rounded-lg border border-gold/30 px-3 py-1 text-xs hover:bg-gold/10"
                        >
                          Review
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {opened && (
        <ReviewModal
          application={opened}
          onClose={() => setOpenId(null)}
          onSave={(vars) => updateMut.mutate({ id: opened.id, ...vars })}
          onDelete={() => {
            if (confirm("Delete this application permanently?")) deleteMut.mutate(opened.id);
          }}
          onGenerateCheckout={(vars) =>
            createCheckoutMut.mutate({ application_id: opened.id, ...vars })
          }
          generating={createCheckoutMut.isPending}
          saving={updateMut.isPending}
        />
      )}
    </div>
  );
}

type SaveVars = {
  status: Status;
  admin_notes: string;
  verified_badge?: boolean;
  payment_link?: string;
  payment_amount_cents?: number;
  payment_currency?: string;
  payment_method?: string;
  payment_reference?: string;
  founding_member?: boolean;
  send_email?: boolean;
};

function ReviewModal({
  application,
  onClose,
  onSave,
  onDelete,
  onGenerateCheckout,
  generating,
  saving,
}: {
  application: Application;
  onClose: () => void;
  onSave: (vars: SaveVars) => void;
  onDelete: () => void;
  onGenerateCheckout: (vars: {
    amount_cents?: number;
    currency?: string;
    openInTab?: boolean;
    label?: string;
  }) => void;
  generating: boolean;
  saving: boolean;
}) {
  const [status, setStatus] = useState<Status>(application.status);
  const [notes, setNotes] = useState(application.admin_notes ?? "");
  const [badge, setBadge] = useState(application.verified_badge);
  const [paymentLink, setPaymentLink] = useState(
    application.payment_link ?? "https://buy.stripe.com/3cI6oHgbC7mO27velqdby00",
  );
  const [amountUsd, setAmountUsd] = useState(
    ((application.payment_amount_cents ?? 6900) / 100).toFixed(0),
  );
  const [currency, setCurrency] = useState(application.payment_currency ?? "USD");
  const [paymentMethod, setPaymentMethod] = useState(application.payment_method ?? "");
  const [paymentReference, setPaymentReference] = useState(application.payment_reference ?? "");
  const [foundingMember, setFoundingMember] = useState(application.founding_member ?? true);
  const [sendEmail, setSendEmail] = useState(true);

  const buildVars = (override?: Partial<SaveVars>): SaveVars => ({
    status,
    admin_notes: notes,
    verified_badge: badge,
    payment_link: paymentLink,
    payment_amount_cents: Math.max(0, Math.round(Number(amountUsd || "0") * 100)),
    payment_currency: currency,
    payment_method: paymentMethod,
    payment_reference: paymentReference,
    founding_member: foundingMember,
    send_email: sendEmail,
    ...override,
  });

  const quickAction = (nextStatus: Status, extra?: Partial<SaveVars>) => {
    setStatus(nextStatus);
    onSave(buildVars({ status: nextStatus, ...extra }));
  };

  const paymentReady = Boolean(paymentLink.trim());

  const subject = encodeURIComponent(
    `Your Recovery Trauma™ therapist application (${application.full_name})`,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4">
      <div className="w-full max-w-3xl rounded-2xl border border-gold/30 bg-card p-6 my-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {application.photo_signed_url ? (
              <img loading="lazy" decoding="async"
                src={application.photo_signed_url}
                alt={application.full_name}
                className="h-16 w-16 rounded-full object-cover ring-2 ring-gold/30"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/10 text-2xl">
                {application.full_name.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold text-foreground">{application.full_name}</h2>
              <p className="text-sm text-muted-foreground">{application.professional_title}</p>
              <p className="text-sm">
                <a href={`mailto:${application.email}`} className="text-blue-500 hover:underline">
                  {application.email}
                </a>
                {application.website && (
                  <>
                    {" · "}
                    <a
                      href={application.website}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-blue-500 hover:underline"
                    >
                      Website
                    </a>
                  </>
                )}
              </p>
              <p className="mt-1 text-xs">
                <span className={`rounded-full px-2 py-0.5 ${STATUS_COLOR[application.status]}`}>
                  {STATUS_LABEL[application.status]}
                </span>
                {application.paid_at && (
                  <span className="ms-2 text-muted-foreground">
                    Paid {new Date(application.paid_at).toLocaleDateString()}
                  </span>
                )}
                {application.published_at && (
                  <span className="ms-2 text-muted-foreground">
                    Published {new Date(application.published_at).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted-foreground hover:bg-background"
            aria-label="Close"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 text-sm">
          <DetailItem label="Country" value={`${application.country}${application.state_region ? `, ${application.state_region}` : ""}`} />
          <DetailItem label="Years experience" value={application.years_experience?.toString() ?? "—"} />
          <DetailItem label="Licensing / Regulatory body" value={application.regulatory_body ?? application.licensing_jurisdiction ?? application.professional_body ?? "—"} />
          <DetailItem label="Licence #" value={application.license_number ?? "—"} />

          <DetailItem label="Insurance confirmed" value={application.insurance_confirmed ? "Yes" : "No"} />
          <DetailItem label="Availability" value={application.availability_modes.join(", ") || "—"} />
          <DetailItem label="Languages" value={application.languages.join(", ") || "—"} />
          <DetailItem label="Approaches" value={application.therapy_approaches.join(", ") || "—"} fullWidth />
          <DetailItem label="Specialisms" value={application.specialisms.join(", ") || "—"} fullWidth />
          <DetailItem label="Qualifications" value={application.qualifications ?? "—"} fullWidth />
          <DetailItem label="Short bio" value={application.short_bio} fullWidth />
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Certificate</div>
            {application.certificate_signed_url ? (
              <a
                href={application.certificate_signed_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-sm text-blue-500 hover:underline"
              >
                View licence / qualification certificate ↗
              </a>
            ) : (
              <div className="text-sm text-muted-foreground">Not provided</div>
            )}
          </div>
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Insurance certificate</div>
            {application.insurance_certificate_signed_url ? (
              <a
                href={application.insurance_certificate_signed_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-sm text-blue-500 hover:underline"
              >
                View insurance certificate ↗
              </a>
            ) : (
              <div className="text-sm text-muted-foreground">Not provided</div>
            )}
          </div>
        </div>

        {/* Founding Therapist Payment */}
        <div className="mt-6 rounded-xl border border-gold/30 bg-gold/5 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg text-foreground">Founding Therapist Payment</h3>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={foundingMember}
                onChange={(e) => setFoundingMember(e.target.checked)}
              />
              Founding member
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                Payment link (Stripe Payment Link, invoice URL, etc.)
              </label>
              <input
                type="url"
                placeholder="https://buy.stripe.com/..."
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                Amount
              </label>
              <input
                type="number"
                min="0"
                value={amountUsd}
                onChange={(e) => setAmountUsd(e.target.value)}
                className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                Currency
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
              >
                <option value="USD">USD</option>

              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                Method
              </label>
              <input
                type="text"
                placeholder="Stripe / bank transfer / …"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                Payment reference / receipt #
              </label>
              <input
                type="text"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Dynamic per-applicant Stripe Checkout */}
          <div className="mt-4 rounded-lg border border-gold/30 bg-background/40 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">
                Generate a unique Stripe Checkout link for this applicant. Metadata
                <code className="mx-1">application_id</code>
                is attached so the webhook auto-marks payment received.
              </div>
              <button
                disabled={generating}
                onClick={() =>
                  onGenerateCheckout({
                    amount_cents: Math.max(
                      100,
                      Math.round(Number(amountUsd || "0") * 100),
                    ),
                    currency,
                  })
                }
                className="inline-flex items-center gap-2 rounded-lg bg-gold px-3 py-2 text-xs font-semibold text-background hover:opacity-90 disabled:opacity-50"
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "💳 Generate Stripe Checkout link"
                )}
              </button>
            </div>

            {/* One-click $1 sandbox test */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-gold/30 bg-gold/5 p-2">
              <div className="text-[11px] text-muted-foreground">
                <strong>Live test:</strong> creates a $1 USD checkout in the live Stripe
                account, opens it in a new tab, then polls until the webhook flips this
                application to <code>payment_received</code> and the confirmation emails
                send. Use card <code>4242 4242 4242 4242</code> on Stripe test mode keys, or
                a real card on live keys (refund afterwards).
              </div>
              <button
                disabled={generating}
                onClick={() =>
                  onGenerateCheckout({
                    amount_cents: 100,
                    currency: "USD",
                    openInTab: true,
                    label: "$1 test checkout",
                  })
                }
                className="inline-flex items-center gap-2 rounded-lg border border-gold bg-background px-3 py-2 text-xs font-semibold text-gold hover:bg-gold/10 disabled:opacity-50"
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "🧪 Run $1 sandbox test"}
              </button>
            </div>

            <div className="mt-2 text-xs">
              Webhook status:{" "}
              {application.paid_at ? (
                <span className="font-semibold text-emerald-500">
                  ✓ payment_received at {new Date(application.paid_at).toLocaleString()}
                </span>
              ) : (
                <span className="text-muted-foreground">awaiting webhook…</span>
              )}
            </div>

            {application.stripe_checkout_session_id && (
              <div className="mt-2 text-xs text-muted-foreground">
                Latest session:{" "}
                <code className="break-all">{application.stripe_checkout_session_id}</code>
                {application.stripe_payment_intent_id && (
                  <> · PaymentIntent: <code className="break-all">{application.stripe_payment_intent_id}</code></>
                )}
              </div>
            )}
          </div>

          {/* Workflow quick actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              disabled={saving || !paymentReady}
              onClick={() => quickAction("payment_pending")}
              title={paymentReady ? "Approve & email payment link" : "Add a payment link first"}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              <CheckCircle2 className="h-4 w-4" /> Approve &amp; send payment link
            </button>
            <button
              disabled={saving}
              onClick={() => quickAction("payment_received")}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              💛 Mark payment received
            </button>
            <button
              disabled={saving}
              onClick={() => quickAction("ready_for_publication")}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              Mark ready for publication
            </button>
            <button
              disabled={saving}
              onClick={() => quickAction("live_in_directory")}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              🚀 Publish profile
            </button>
            <button
              disabled={saving}
              onClick={() => quickAction("pending_review")}
              className="inline-flex items-center gap-2 rounded-lg border border-gold/30 px-3 py-2 text-xs hover:bg-gold/10 disabled:opacity-40"
              title="Unpublish — return to pending review"
            >
              Unpublish
            </button>
            <button
              disabled={saving}
              onClick={() => quickAction("rejected")}
              className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-40"
            >
              <XCircle className="h-4 w-4" /> Reject
            </button>
          </div>
        </div>

        <div className="mt-6 space-y-4 border-t border-gold/20 pt-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as Status)}
              className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
            >
              {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Admin notes (visible to admin only)
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border border-gold/30 bg-background px-3 py-2 text-sm"
              placeholder="Reason for status change, credentials checked, etc."
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={badge}
                onChange={(e) => setBadge(e.target.checked)}
              />
              Recovery Trauma Verified badge
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              Send transition email to applicant
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <div className="flex flex-wrap gap-2">
              <a
                href={`mailto:${application.email}?subject=${subject}&body=${encodeURIComponent(
                  `Hi ${application.full_name},\n\nThank you for applying to the Recovery Trauma™ directory.\n\n`,
                )}`}
                className="inline-flex items-center gap-2 rounded-lg border border-gold/30 px-3 py-2 text-sm hover:bg-gold/10"
              >
                <Mail className="h-4 w-4" /> Email applicant
              </a>
              <button
                onClick={onDelete}
                className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </div>
            <button
              onClick={() => onSave(buildVars())}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-background hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: string;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 whitespace-pre-wrap text-foreground">{value}</div>
    </div>
  );
}
